import React from "react";
import { Bell, BellOff } from "lucide-react";
import styled from "styled-components";
import { useNotification } from "../contexts/notification";
import { useLanguage } from "../contexts/language";
import {
  NotificationPromptModal,
  useNotificationPrompt,
} from "./notification-prompt-modal";

const ToggleLabel = styled.span`
  display: none;

  @media (min-width: 480px) {
    display: inline;
  }
`;

const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  border-radius: 8px;
  border: 1px solid ${(props) => (props.$active ? "#c9a961" : "#e5e5e5")};
  background-color: ${(props) => (props.$active ? "rgba(201, 169, 97, 0.1)" : "#f9f9f9")};
  padding: 6px 8px;
  font-size: 13px;
  font-weight: 500;
  color: #333;
  transition: all 0.2s;
  cursor: pointer;

  @media (min-width: 480px) {
    padding: 8px 12px;
    font-size: 14px;
  }

  &:hover:not(:disabled) {
    background-color: rgba(201, 169, 97, 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const NotificationToggle = ({ autoPrompt = false }) => {
  const { notificationState, subscribe } = useNotification();
  const { t } = useLanguage();
  const [isSubscribing, setIsSubscribing] = React.useState(false);
  const { promptMode, showDeniedModal, closePromptModal } = useNotificationPrompt();
  const [showPrompt, setShowPrompt] = React.useState(false);

  // Auto-prompt: show prompt modal on mount for fresh browsers (only when autoPrompt=true)
  React.useEffect(() => {
    if (autoPrompt && promptMode === "prompt") {
      setShowPrompt(true);
    }
  }, [autoPrompt, promptMode]);

  if (notificationState === "unsupported" || notificationState === "loading") {
    return null;
  }

  const isActive = notificationState === "subscribed";
  const Icon = isActive ? Bell : BellOff;

  const label = (() => {
    if (notificationState === "denied") return t.notifications.blocked;
    if (isActive) return t.notifications.on;
    return t.notifications.off;
  })();

  const handleClick = async () => {
    if (isActive) {
      window.alert(t.notifications.alreadyEnabled);
      return;
    }

    if (notificationState === "denied") {
      showDeniedModal();
      setShowPrompt(true);
      return;
    }

    // "prompt" or "unsubscribed" — attempt to subscribe
    setIsSubscribing(true);
    try {
      await subscribe();
    } finally {
      setIsSubscribing(false);
    }
  };

  const handleCloseModal = () => {
    setShowPrompt(false);
    closePromptModal();
  };

  return (
    <>
      <ToggleButton
        onClick={handleClick}
        disabled={isSubscribing}
        $active={isActive}
        aria-label="Toggle notifications"
      >
        <Icon />
        <ToggleLabel>{label}</ToggleLabel>
      </ToggleButton>

      {showPrompt && promptMode && (
        <NotificationPromptModal mode={promptMode} onClose={handleCloseModal} />
      )}
    </>
  );
};
